<script setup>
import { ref, computed, watch, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import BoletoClientsAPI from 'dashboard/api/boletoClients';

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  client: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close', 'save', 'refresh']);

const { t } = useI18n();

// Tabs
const activeTab = ref('config');

// Form data
const selectedInboxId = ref(null);
const customMessage = ref('');
const sendDay = ref(5);
const sendTime = ref('09:00');
const sendCount = ref(1);
const sendIntervalDays = ref(2);
const respectBusinessHours = ref(true);
const active = ref(true);

// Files data
const files = ref([]);
const loadingFiles = ref(false);
const deletingFileId = ref(null);

// Ref for textarea
const messageTextarea = useTemplateRef('messageTextarea');

// Message variables (only those we have data for)
const MESSAGE_VARIABLES = [
  { variable: '{nome}', label: 'OMNI.BOLETOS.VARIABLES.NAME', icon: 'i-lucide-user' },
  { variable: '{mes_referencia}', label: 'OMNI.BOLETOS.VARIABLES.REFERENCE_MONTH', icon: 'i-lucide-calendar' },
  { variable: '{dia_envio}', label: 'OMNI.BOLETOS.VARIABLES.SEND_DAY', icon: 'i-lucide-calendar-clock' },
];

// Data from store
const inboxes = useMapGetter('inboxes/getInboxes');

// Filtered inboxes
const supportedInboxes = computed(() => {
  return inboxes.value.filter(inbox => {
    const type = inbox.channel_type;
    return ['Channel::Whatsapp', 'Channel::TwilioSms', 'Channel::Sms', 'Channel::Api', 'Channel::Email'].includes(type);
  });
});

// Get selected inbox object
const selectedInbox = computed(() => {
  if (!selectedInboxId.value) return null;
  return inboxes.value.find(inbox => inbox.id === selectedInboxId.value);
});

// Check if selected inbox has working hours enabled
const inboxHasWorkingHours = computed(() => {
  return selectedInbox.value?.working_hours_enabled || false;
});

// Get working days from inbox (days that are not closed_all_day)
const workingDaysOfWeek = computed(() => {
  if (!selectedInbox.value?.working_hours) return [0, 1, 2, 3, 4, 5, 6];
  return selectedInbox.value.working_hours
    .filter(wh => !wh.closed_all_day)
    .map(wh => wh.day_of_week);
});

// Helper to check if a day of month falls on a working day
const isWorkingDay = (dayOfMonth) => {
  if (!inboxHasWorkingHours.value || !respectBusinessHours.value) return true;
  
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const lastDay = new Date(year, month + 1, 0).getDate();
  const actualDay = Math.min(dayOfMonth, lastDay);
  const date = new Date(year, month, actualDay);
  const dayOfWeek = date.getDay();
  
  return workingDaysOfWeek.value.includes(dayOfWeek);
};

// Find nearest working day (looking backwards first, then forwards)
const findNearestWorkingDay = (dayOfMonth) => {
  if (isWorkingDay(dayOfMonth)) return dayOfMonth;
  
  for (let offset = 1; offset <= 7; offset++) {
    let newDay = dayOfMonth - offset;
    if (newDay <= 0) newDay = 28 + newDay;
    if (isWorkingDay(newDay)) return newDay;
  }
  
  for (let offset = 1; offset <= 7; offset++) {
    let newDay = dayOfMonth + offset;
    if (newDay > 28) newDay = newDay - 28;
    if (isWorkingDay(newDay)) return newDay;
  }
  
  return dayOfMonth;
};

// Calculate effective send days considering business hours
const effectiveSendDays = computed(() => {
  const baseDays = calculatedSendDays.value;
  
  if (!inboxHasWorkingHours.value || !respectBusinessHours.value) {
    return baseDays;
  }
  
  return [...new Set(baseDays.map(day => findNearestWorkingDay(day)))].sort((a, b) => a - b);
});

// Check if any send day was adjusted due to business hours
const hasSendDaysAdjusted = computed(() => {
  if (!inboxHasWorkingHours.value || !respectBusinessHours.value) return false;
  const base = calculatedSendDays.value;
  const effective = effectiveSendDays.value;
  if (base.length !== effective.length) return true;
  return base.some((day, i) => day !== effective[i]);
});

// Insert variable into message
const insertVariable = variable => {
  const textarea = messageTextarea.value;
  if (!textarea) {
    customMessage.value = (customMessage.value || '') + variable;
    return;
  }
  
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const currentValue = customMessage.value || '';
  
  customMessage.value = 
    currentValue.substring(0, start) + 
    variable + 
    currentValue.substring(end);
  
  const newCursorPos = start + variable.length;
  setTimeout(() => {
    textarea.focus();
    textarea.setSelectionRange(newCursorPos, newCursorPos);
  }, 0);
};

// Fetch files for client
const fetchFiles = async () => {
  if (!props.client) return;
  
  loadingFiles.value = true;
  try {
    const response = await BoletoClientsAPI.getFiles(props.client.id);
    files.value = response.data || [];
  } catch (error) {
    console.error('Error fetching files:', error);
    files.value = [];
  } finally {
    loadingFiles.value = false;
  }
};

// Delete file
const deleteFile = async (fileId) => {
  if (!confirm('Tem certeza que deseja excluir este arquivo?')) return;
  
  deletingFileId.value = fileId;
  try {
    await BoletoClientsAPI.deleteFile(props.client.id, fileId);
    files.value = files.value.filter(f => f.id !== fileId);
    useAlert('Arquivo removido com sucesso');
    emit('refresh');
  } catch (error) {
    useAlert(error.response?.data?.message || 'Erro ao remover arquivo');
  } finally {
    deletingFileId.value = null;
  }
};

// Get file download URL
const getFileUrl = (file) => {
  if (file.file_url) return file.file_url;
  return `/rails/active_storage/blobs/${file.blob_signed_id}/${file.original_filename}`;
};

// Get status badge class
const getStatusClass = (status, filePurged = false) => {
  if (filePurged) {
    return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400';
  }
  switch (status) {
    case 'sent':
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    case 'pending':
      return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
    case 'failed':
      return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
    default:
      return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400';
  }
};

// Get status label
const getStatusLabel = (status, filePurged = false) => {
  if (filePurged) return 'Concluído';
  switch (status) {
    case 'sent': return 'Enviado';
    case 'pending': return 'Pendente';
    case 'failed': return 'Falhou';
    case 'expired': return 'Expirado';
    case 'cancelled': return 'Cancelado';
    default: return status;
  }
};

// Format month label
const getMonthLabel = (referenceMonth) => {
  if (!referenceMonth) return '-';
  const [year, month] = referenceMonth.split('-');
  const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  return `${monthNames[parseInt(month) - 1]}/${year}`;
};

// Format date
const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR');
};

// Populate form when client changes
watch(
  () => props.client,
  newClient => {
    if (newClient) {
      selectedInboxId.value = newClient.inbox_id;
      customMessage.value = newClient.custom_message || '';
      sendDay.value = newClient.send_day;
      sendTime.value = newClient.send_time;
      sendCount.value = newClient.send_count || 1;
      sendIntervalDays.value = newClient.send_interval_days || 2;
      respectBusinessHours.value = newClient.respect_business_hours ?? true;
      active.value = newClient.active;
      
      // Fetch files when opening modal
      fetchFiles();
    }
  },
  { immediate: true }
);

// Reset tab when modal opens
watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      activeTab.value = 'config';
      fetchFiles();
    }
  }
);

const isValid = computed(() => {
  return selectedInboxId.value && sendDay.value && sendTime.value;
});

const handleSubmit = () => {
  if (!isValid.value) return;

  emit('save', {
    inbox_id: selectedInboxId.value,
    custom_message: customMessage.value || null,
    send_day: sendDay.value,
    send_time: sendTime.value,
    send_count: sendCount.value,
    send_interval_days: sendIntervalDays.value,
    respect_business_hours: respectBusinessHours.value,
    active: active.value,
  });
};

// Calculate send days for preview
const calculatedSendDays = computed(() => {
  if (sendCount.value <= 1) return [sendDay.value];
  
  const days = [];
  for (let i = 0; i < sendCount.value; i++) {
    let day = sendDay.value - (sendIntervalDays.value * (sendCount.value - 1 - i));
    day = day <= 0 ? 28 + day : day;
    days.push(day);
  }
  return days;
});

const dayOptions = Array.from({ length: 28 }, (_, i) => i + 1);

// Stats computed
const pendingCount = computed(() => files.value.filter(f => f.status === 'pending').length);
const sentCount = computed(() => files.value.filter(f => f.status === 'sent').length);
const failedCount = computed(() => files.value.filter(f => f.status === 'failed').length);
</script>

<template>
  <div
    v-if="isOpen && client"
    class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
    @click.self="emit('close')"
  >
    <div
      class="bg-n-solid-2 rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col"
    >
      <!-- Header -->
      <div
        class="flex items-center justify-between p-4 border-b border-n-weak"
      >
        <div>
          <h3 class="text-lg font-semibold text-n-slate-12">
            {{ t('OMNI.BOLETOS.CONFIG_MODAL.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11">{{ client.contact.name }}</p>
        </div>
        <button
          class="p-1 text-n-slate-10 hover:text-n-slate-12"
          @click="emit('close')"
        >
          <span class="i-lucide-x text-xl" />
        </button>
      </div>

      <!-- Tabs -->
      <div class="flex border-b border-n-weak px-4">
        <button
          class="px-4 py-3 text-sm font-medium border-b-2 transition-colors"
          :class="activeTab === 'config' 
            ? 'border-woot-500 text-woot-600 dark:text-woot-400' 
            : 'border-transparent text-n-slate-10 hover:text-n-slate-12'"
          @click="activeTab = 'config'"
        >
          <span class="i-lucide-settings mr-1.5" />
          Configurações
        </button>
        <button
          class="px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2"
          :class="activeTab === 'files' 
            ? 'border-woot-500 text-woot-600 dark:text-woot-400' 
            : 'border-transparent text-n-slate-10 hover:text-n-slate-12'"
          @click="activeTab = 'files'"
        >
          <span class="i-lucide-files mr-1.5" />
          Arquivos
          <span 
            v-if="files.length > 0"
            class="px-1.5 py-0.5 text-xs rounded-full bg-n-alpha-3 text-n-slate-11"
          >
            {{ files.length }}
          </span>
        </button>
      </div>

      <!-- Content -->
      <div class="p-4 overflow-y-auto flex-1">
        <!-- Config Tab -->
        <div v-if="activeTab === 'config'" class="space-y-4">
          <!-- Inbox Selection -->
          <div>
            <label
              class="block text-sm font-medium text-n-slate-12 mb-1"
            >
              {{ t('OMNI.BOLETOS.CONFIG_MODAL.INBOX') }}
            </label>
            <select
              v-model="selectedInboxId"
              class="w-full px-3 py-2 border border-n-weak rounded-lg bg-n-alpha-3 text-n-slate-12"
            >
              <option
                v-for="inbox in supportedInboxes"
                :key="inbox.id"
                :value="inbox.id"
              >
                {{ inbox.name }}
              </option>
            </select>
          </div>

          <!-- Send Schedule -->
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label
                class="block text-sm font-medium text-n-slate-12 mb-1"
              >
                {{ t('OMNI.BOLETOS.CONFIG_MODAL.SEND_DAY') }}
              </label>
              <select
                v-model="sendDay"
                class="w-full px-3 py-2 border border-n-weak rounded-lg bg-n-alpha-3 text-n-slate-12"
              >
                <option v-for="day in dayOptions" :key="day" :value="day">
                  {{ t('OMNI.BOLETOS.CONFIG_MODAL.DAY') }} {{ day }}
                </option>
              </select>
            </div>
            <div>
              <label
                class="block text-sm font-medium text-n-slate-12 mb-1"
              >
                {{ t('OMNI.BOLETOS.CONFIG_MODAL.SEND_TIME') }}
              </label>
              <input
                v-model="sendTime"
                type="time"
                class="w-full px-3 py-2 border border-n-weak rounded-lg bg-n-alpha-3 text-n-slate-12"
              />
            </div>
          </div>
          <p class="text-xs text-n-slate-11">
            {{ t('OMNI.BOLETOS.CONFIG_MODAL.SCHEDULE_HINT') }}
          </p>

          <!-- Single send day adjusted warning -->
          <div
            v-if="sendCount === 1 && inboxHasWorkingHours && respectBusinessHours && hasSendDaysAdjusted"
            class="mt-2 p-2 bg-amber-50 dark:bg-amber-900/30 rounded text-xs text-amber-700 dark:text-amber-300"
          >
            <span class="i-lucide-alert-triangle mr-1" />
            {{ t('OMNI.BOLETOS.ADD_CLIENT_MODAL.SEND_DAY_ADJUSTED', { original: sendDay, effective: effectiveSendDays[0] }) }}
          </div>

          <!-- Multi-send Configuration -->
          <div class="mt-4 p-4 bg-n-alpha-2 rounded-lg">
            <label class="block text-sm font-medium text-n-slate-12 mb-3">
              Configuração de Múltiplos Envios
            </label>
            
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-xs text-n-slate-11 mb-1">
                  Número de envios
                </label>
                <select
                  v-model="sendCount"
                  class="w-full px-3 py-2 text-sm border border-n-weak rounded-lg bg-n-alpha-3 text-n-slate-12"
                >
                  <option :value="1">1 envio (único)</option>
                  <option :value="2">2 envios</option>
                  <option :value="3">3 envios</option>
                </select>
              </div>
              <div>
                <label class="block text-xs text-n-slate-11 mb-1">
                  Intervalo entre envios
                </label>
                <select
                  v-model="sendIntervalDays"
                  :disabled="sendCount === 1"
                  class="w-full px-3 py-2 text-sm border border-n-weak rounded-lg bg-n-alpha-3 text-n-slate-12 disabled:opacity-50"
                >
                  <option :value="2">2 dias</option>
                  <option :value="3">3 dias</option>
                </select>
              </div>
            </div>

            <!-- Preview of send days -->
            <div v-if="sendCount > 1" class="mt-3 p-2 bg-blue-50 dark:bg-blue-900/30 rounded text-xs text-blue-700 dark:text-blue-300">
              <span class="i-lucide-info mr-1" />
              {{ t('OMNI.BOLETOS.CONFIG_MODAL.SEND_DAYS_LABEL') }}: <strong>{{ effectiveSendDays.join(', ') }}</strong>
              <br/>
              <span class="text-blue-600 dark:text-blue-400">
                ({{ sendCount }}° envio no dia {{ sendDay }}, anteriores com {{ sendIntervalDays }} dias de intervalo)
              </span>
              <div v-if="hasSendDaysAdjusted" class="mt-1 text-amber-600 dark:text-amber-400">
                <span class="i-lucide-alert-triangle mr-1" />
                {{ t('OMNI.BOLETOS.CONFIG_MODAL.DAYS_ADJUSTED_HINT') }}
              </div>
            </div>
          </div>

          <!-- Business Hours Option -->
          <div class="p-4 bg-n-alpha-2 rounded-lg">
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                v-model="respectBusinessHours"
                type="checkbox"
                class="w-4 h-4 rounded border-n-weak text-woot-500 focus:ring-woot-500"
              />
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('OMNI.BOLETOS.CONFIG_MODAL.RESPECT_BUSINESS_HOURS') }}
              </span>
            </label>
            <p class="mt-1 text-xs text-n-slate-11 ml-6">
              {{ t('OMNI.BOLETOS.CONFIG_MODAL.RESPECT_BUSINESS_HOURS_HINT') }}
            </p>

            <!-- Info when inbox doesn't have working hours -->
            <div
              v-if="!inboxHasWorkingHours && respectBusinessHours"
              class="mt-2 p-2 bg-blue-50 dark:bg-blue-900/30 rounded text-xs text-blue-700 dark:text-blue-300"
            >
              <span class="i-lucide-info mr-1" />
              {{ t('OMNI.BOLETOS.CONFIG_MODAL.NO_WORKING_HOURS_INFO') }}
            </div>
          </div>

          <!-- Custom Message with Variables -->
          <div>
            <label
              class="block text-sm font-medium text-n-slate-12 mb-1"
            >
              {{ t('OMNI.BOLETOS.CONFIG_MODAL.MESSAGE') }}
            </label>
            <textarea
              ref="messageTextarea"
              v-model="customMessage"
              rows="4"
              :placeholder="t('OMNI.BOLETOS.CONFIG_MODAL.MESSAGE_PLACEHOLDER')"
              class="w-full px-3 py-2 border border-n-weak rounded-lg bg-n-alpha-3 text-n-slate-12 placeholder:text-n-slate-10 resize-none"
            />
            
            <!-- Variable buttons -->
            <div class="mt-2">
              <p class="text-xs text-n-slate-11 mb-2">
                {{ t('OMNI.BOLETOS.CONFIG_MODAL.INSERT_VARIABLES') }}
              </p>
              <div class="flex flex-wrap gap-1.5">
                <button
                  v-for="varItem in MESSAGE_VARIABLES"
                  :key="varItem.variable"
                  type="button"
                  class="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md
                         bg-n-alpha-3 hover:bg-n-slate-4 
                         text-n-slate-11 hover:text-n-slate-12
                         border border-n-weak transition-all"
                  @click="insertVariable(varItem.variable)"
                >
                  <span :class="varItem.icon" class="size-3" />
                  {{ t(varItem.label) }}
                </button>
              </div>
            </div>
          </div>

          <!-- Active Toggle -->
          <div class="flex items-center justify-between">
            <div>
              <label
                class="block text-sm font-medium text-n-slate-12"
              >
                {{ t('OMNI.BOLETOS.CONFIG_MODAL.ACTIVE') }}
              </label>
              <p class="text-xs text-n-slate-11">
                {{ t('OMNI.BOLETOS.CONFIG_MODAL.ACTIVE_HINT') }}
              </p>
            </div>
            <Switch
              :model-value="active"
              @update:model-value="active = $event"
            />
          </div>
        </div>

        <!-- Files Tab -->
        <div v-else-if="activeTab === 'files'">
          <!-- Stats Summary -->
          <div class="grid grid-cols-3 gap-3 mb-4">
            <div class="bg-yellow-50 dark:bg-yellow-900/30 rounded-lg p-3 text-center">
              <div class="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{{ pendingCount }}</div>
              <div class="text-xs text-yellow-600/70 dark:text-yellow-400/70">Pendentes</div>
            </div>
            <div class="bg-green-50 dark:bg-green-900/30 rounded-lg p-3 text-center">
              <div class="text-2xl font-bold text-green-600 dark:text-green-400">{{ sentCount }}</div>
              <div class="text-xs text-green-600/70 dark:text-green-400/70">Enviados</div>
            </div>
            <div class="bg-red-50 dark:bg-red-900/30 rounded-lg p-3 text-center">
              <div class="text-2xl font-bold text-red-600 dark:text-red-400">{{ failedCount }}</div>
              <div class="text-xs text-red-600/70 dark:text-red-400/70">Falhas</div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loadingFiles" class="flex items-center justify-center py-8">
            <span class="i-lucide-loader-2 w-6 h-6 animate-spin text-woot-500" />
          </div>

          <!-- Empty State -->
          <div v-else-if="files.length === 0" class="text-center py-8">
            <span class="i-lucide-file-x text-4xl text-n-slate-8 mb-2 block mx-auto" />
            <p class="text-n-slate-11 text-sm">Nenhum arquivo carregado</p>
            <p class="text-n-slate-10 text-xs mt-1">Use o botão de upload no card do cliente</p>
          </div>

          <!-- Files List -->
          <div v-else class="space-y-2">
            <div
              v-for="file in files"
              :key="file.id"
              class="flex items-center gap-3 p-3 bg-n-alpha-2 rounded-lg border border-n-weak"
            >
              <!-- File Icon -->
              <div class="w-10 h-10 flex items-center justify-center bg-red-100 dark:bg-red-900/30 rounded-lg">
                <span class="i-lucide-file-text text-red-600 dark:text-red-400 text-xl" />
              </div>

              <!-- File Info -->
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2">
                  <span class="text-sm font-medium text-n-slate-12 truncate">
                    {{ getMonthLabel(file.reference_month) }}
                  </span>
                  <span 
                    class="px-1.5 py-0.5 text-xs rounded-full"
                    :class="getStatusClass(file.status, file.file_purged)"
                  >
                    {{ getStatusLabel(file.status, file.file_purged) }}
                  </span>
                </div>
                <div class="text-xs text-n-slate-11 truncate mt-0.5">
                  {{ file.original_filename }}
                </div>
                <div v-if="file.sent_at" class="text-xs text-n-slate-10 mt-0.5">
                  Enviado em {{ formatDate(file.sent_at) }}
                </div>
                <div v-if="file.file_purged" class="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
                  <span class="i-lucide-check-circle text-green-500" />
                  Arquivo excluído após conclusão dos envios
                </div>
                <div v-else-if="file.error_message" class="text-xs text-red-500 mt-0.5 truncate">
                  Erro: {{ file.error_message }}
                </div>
              </div>

              <!-- Actions -->
              <div class="flex items-center gap-1">
                <!-- Download (only if file not purged) -->
                <a
                  v-if="file.file_url && !file.file_purged"
                  :href="file.file_url"
                  target="_blank"
                  class="p-2 text-n-slate-10 hover:text-blue-500 rounded-lg bg-n-alpha-3 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
                  title="Baixar arquivo"
                >
                  <span class="i-lucide-download text-lg" />
                </a>
                
                <!-- File purged indicator -->
                <div
                  v-if="file.file_purged"
                  class="p-2 text-green-500 rounded-lg bg-green-50 dark:bg-green-900/30"
                  title="Todos os envios concluídos"
                >
                  <span class="i-lucide-check-circle text-lg" />
                </div>
                
                <!-- Delete (only if file not purged) -->
                <button
                  v-if="!file.file_purged"
                  class="p-2 text-n-slate-10 hover:text-red-500 rounded-lg bg-n-alpha-3 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors disabled:opacity-50"
                  :disabled="deletingFileId === file.id"
                  title="Excluir arquivo"
                  @click="deleteFile(file.id)"
                >
                  <span 
                    :class="deletingFileId === file.id ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-trash-2'" 
                    class="text-lg" 
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="flex items-center justify-end gap-2 p-4 border-t border-n-weak"
      >
        <button
          class="px-4 py-2 text-sm font-medium text-n-slate-12 bg-n-alpha-3 rounded-lg hover:bg-n-slate-4"
          @click="emit('close')"
        >
          {{ t('OMNI.BOLETOS.CANCEL') }}
        </button>
        <button
          v-if="activeTab === 'config'"
          class="px-4 py-2 text-sm font-medium text-white bg-woot-500 rounded-lg hover:bg-woot-600 disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="!isValid"
          @click="handleSubmit"
        >
          {{ t('OMNI.BOLETOS.CONFIG_MODAL.SAVE') }}
        </button>
      </div>
    </div>
  </div>
</template>
